















































































import { Component, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"

import { Portal } from "portal-vue"

import CaseList from "@/components/New/Cases/Overview/CaseList.vue"
import ExpandableSidebar from "@/components/New/Page/ExpandableSidebar.vue"

import { ViewsService } from "@/services/viewsService"

import { User, Case } from "@/types"
import { ViewFullSummary, ViewSummary } from "@/types/views/viewSummary"
import ViewFolders from "@/components/New/Folders/ViewFolders.vue"
import { Folder, FolderType, FolderItem } from "@/types/folder"
import api from "@/services/api"
import DataCache from "@/caching/caching"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

@Component({
	components: {
		"case-list": CaseList,
		"sidebar": ExpandableSidebar,
		"portal": Portal,
		"view-folders": ViewFolders
	}
})
export default class Overview extends Vue {
	@State("userCache") private readonly userCache!: DataCache<User>;
	@Getter("getMe") private readonly me!: User;
	@Getter("getRecordName") private readonly recordName!: string;
	@Getter("getPluralizedRecordName") private readonly pluralRecordName!: string;

	@caseStore.State private viewsService!: ViewsService;
	@caseStore.Action private casesLoad!: () => void;
	@caseStore.Action private caseClear!: () => void;
	@caseStore.Action("detailsLoad") private detailsLoad!: () => void;
	@caseStore.Mutation("setActiveViewId") private setActiveViewId!: (viewId: string | null) => void;

	@setupStore.Action private loadFieldsIfUnloaded!: () => Promise<void>;

	private viewsSearchFilter: string = "";
	private caseSearchFilter: string = "";
	private filteredCases: Case[] = [];
	private folders: Folder[] = []

	private isMounted: boolean = false

	/**
	 * @summary Gets the summary for the currently selected view.
	 */
	private get viewSummary (): ViewFullSummary | null {
		return this.viewsService.viewSummary
	}

	/**
	 * @summary filters the views based on the provided filter term and returns the result as a FolderItem[]
	 */
	private get filteredViews (): FolderItem[] | undefined {
		const lowerFilter = this.viewsSearchFilter ? this.viewsSearchFilter.toLowerCase() : ""
		const filtered = this.viewsSearchFilter
			? this.viewSummary?.summary?.filter(i => i.title.toLowerCase().includes(lowerFilter))
			: this.viewSummary?.summary

		return filtered?.map((fv: ViewSummary) => {
			return {
				...fv,
				itemCount: fv.caseCount
			}
		}) as FolderItem[]
	}

	/**
	 * @summary Gets the number of cases in the current view.
	 */
	private get recordCount (): number {
		return this.viewSummary?.cases?.length ?? 0
	}

	/**
	 * @summary Gets the title for the currently selected view.
	 */
	private get viewTitle (): string {
		return this.viewSummary?.activeView?.title ?? "Records"
	}

	/**
	 * @summary Gets the text to use when displaying the number of records help text.
	 */
	private get caseTotalText (): string {
		if (this.viewSummary?.cases && this.caseSearchFilter && /\S/.test(this.caseSearchFilter)) {
			return `Showing ${this.filteredCases.length} of ${this.recordCount} records`
		} else if (this.viewSummary?.cases) {
			return `${this.recordCount} records`
		} else {
			return ""
		}
	}

	/**
	 * @summary Mounted lifecycle hook.
	 */
	public async mounted () {
		this.caseClear()
		this.detailsLoad()
		const loadUsersPromise = this.userCache.fetchIfNeeded()
		const loadFieldsPromise = this.loadFieldsIfUnloaded()
		const loadFoldersPromise = this.loadFolders()

		await Promise.allSettled([loadUsersPromise, loadFieldsPromise])

		let loadViewsPromise: Promise<void> | null = null
		if (this.viewsService) {
			if (this.$route.params.viewId) {
				this.viewsService.activeViewId = this.$route.params.viewId
			}

			loadViewsPromise = this.viewsService.loadViews()
		}

		await Promise.allSettled([loadFoldersPromise, loadViewsPromise])

		this.isMounted = true
	}

	// Retrieve all folders
	private async loadFolders () {
		this.folders = await api.foldersGet(FolderType.View)
	}

	/**
	 * @summary Handler for when the list of filtered cases is updated within the case list component.
	 */
	private onFilteredCasesUpdated (filteredCases: Case[]): void {
		this.filteredCases = filteredCases
	}

	/**
	 * @summary Watcher fired when the selected view is changed via the route.
	 */
	@Watch("$route.params.viewId")
	private async onViewChanged (newViewId: string | null) {
		if (!newViewId) {
			return
		}

		this.viewsService.activeViewId = newViewId
		await this.viewsService.loadViews()
	}
}
